<template>
  <section>
    <summary-block
      :properties="financingSummary"
      :is-loading="financingIsLoading"
      data-qa="insurance-block"
      @show-in-pdf="toggleShowInPdf"
      @show-config-modal="checkInsuranceRequiredParameters(openConfigModal, closeConfigModal)"
    />

    <modal
      v-if="showFinancialServiceConfig"
      id="insurance"
      :max-width="wideCoverageNames ? '730px' : ''"
      :title="retrieveModalText('title')"
      @close="closeConfigModal"
    >
      <insurance-form
        :financial-params="computedFinancialParams"
        :product-types="productTypes"
        :is-read-only="isReadOnly"
        :dictionary-folder="productTypeDictionaryFolder"
        :is-loading="financingIsLoading"
        :is-grey-licence-plate="isGreyLicencePlate"
        :new-value-regulation-fixed="newValueRegulationFixed"
        :wide-coverage-names="wideCoverageNames"
        @update-financing="updateFinancingParams"
        @submit="closeConfigModal"
        @search-coc-number="searchCocNumber"
      />
    </modal>
    <confirmation-prompt
      id="insurance-required-parameters-prompt"
      :show="showInsuranceRequiredParameterPrompt || searchCompanyIsPending"
      :info-text="retrieveModalText('confirmdateofbirthanddamagefreeyears')"
      :align-buttons="'center'"
      :cancel-button-show="false"
      :disabled="!allRequiredFieldsProvided"
      @confirm="onInsuranceRequiredParameterPromptConfirm"
      @close="closeInsuranceRequiredParameterPrompt(true)"
    >
      <div id="insurance-required-parameters-prompt-body" class="prompt-body">
        <form-input
          v-if="isGreyLicencePlate"
          id="cocNumber"
          v-model.lazy="computedFinancialParams[paramKeys.cocNumber]"
          :title="retrieveModalText('cocNumber')"
          type="search"
          class="no-info"
          :disabled="disableFields"
          @blur="changeCocNumber($event)"
        >
        </form-input>
        <date-picker
          id="dateOfBirth"
          :title="
            retrieveModalText(
              isBusinessCustomer ? 'maindriverdateofbirthlabel' : 'dateofbirthlabel'
            )
          "
          :date="computedFinancialParams[paramKeys.dateOfBirth]"
          :min-date="minDate"
          :show-unintended-cancel-notification="false"
          :disabled="disableFields"
          @change-date="updateDateOfBirth($event, false)"
        >
          <template #info>
            <info-modal
              :title="retrieveInfoText('dateofbirthinfotitle')"
              :content="retrieveInfoText('dateofbirthinfotext')"
              data-qa="date-of-birth-info-icon"
            />
          </template>
        </date-picker>
        <form-input
          id="damageFreeYears"
          v-model.lazy="computedFinancialParams[paramKeys.damageFreeYears]"
          :title="retrieveModalText('damagefreeyearslabel')"
          type="text"
          :disabled="disableFields"
          @blur="updateFinancingParams(paramKeys.damageFreeYears, $event, false)"
        >
          <template #info>
            <info-modal
              :title="retrieveInfoText('damagefreeyearsinfotitle')"
              :content="retrieveInfoText('damagefreeyearsinfotext')"
              data-qa="damage-free-years-info-icon"
            />
          </template>
        </form-input>
      </div>
    </confirmation-prompt>
    <confirmation-prompt
      id="insurance-company-data-correct-prompt"
      :show="showCompanyDataCorrectPrompt"
      :info-text="retrieveModalText('informationCorrect')"
      :align-buttons="'center'"
      :cancel-button-show="false"
      @confirm="onCompanyDataCorrectPromptConfirm"
      @close="onCompanyDataCorrectPromptCancel"
    >
      <div id="insurance-company-data-correct-prompt-body" class="prompt-body">
        <form-input
          v-if="isGreyLicencePlate"
          id="cocNumber2"
          v-model.lazy="computedFinancialParams[paramKeys.cocNumber]"
          :title="retrieveModalText('cocNumber')"
          type="search"
          class="no-info"
          :disabled="disableFields"
          @blur="changeCocNumber($event)"
        >
        </form-input>
        <form-input
          id="companyName"
          v-model.lazy="computedFinancialParams[paramKeys.companyName]"
          :title="retrieveModalText('companyName')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @blur="updateFinancingParams(paramKeys.companyName, $event, false)"
        >
        </form-input>

        <form-input
          id="sbiCode"
          v-model.lazy="computedFinancialParams[paramKeys.sbiCode]"
          :title="retrieveModalText('sbiCode')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @blur="updateFinancingParams(paramKeys.sbiCode, $event, false)"
        >
        </form-input>

        <form-textarea
          id="sbiCodeText"
          v-model.lazy="computedFinancialParams[paramKeys.sbiCodeText]"
          :title="retrieveModalText('sbiCodeName')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @blur="updateFinancingParams(paramKeys.sbiCodeText, $event, false)"
        >
        </form-textarea>
      </div>
    </confirmation-prompt>
  </section>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import SummaryBlock from '@/components/generic/container/FinancialServices/SummaryBlock.vue'
import FinancialServices from '@/api/server/FinancialServices'
import { computed } from 'vue'
import { mapGetters, mapActions } from 'vuex'
import FinancialServiceBlock from '@/mixins/financialServices/financialServiceBlock'
import InsuranceForm from '@/components/generic/container/FinancialServices/Insurance/InsuranceForm.vue'
import { EventBus } from '@/plugins/event-bus'
import CurrencyHelper from '@/helpers/currencyHelper'
import moment from 'moment'
import { extend } from 'lodash'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import InfoModal from '@/components/generic/presentational/atoms/infoModal/InfoModal.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import Companies from '@/api/server/Companies'
import FormTextarea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'
import { parseCoverageOptions, findCoverageOptionByCode } from '@/helpers/insuranceCoverageOptions'

// Insurance modal texts that differ per insurance provider. These are resolved from a
// provider-namespaced key (e.g. `.../vwpfs/...` or `.../vmfs/...`) with NO cross-provider fallback,
// so one provider's copy can never leak into another. Every other label stays shared/brand-based
// (the `*`/brand_code rows). Keys are the `labelName` as passed to retrieveText (i.e. after the
// `/financialservices/insurance/` prefix is added).
const INSURANCE_PROVIDER_SPECIFIC_LABELS = new Set([
  'modal/disclaimertext',
  'modal/info/maincoverageinfotext',
  'modal/info/newvalueregulationinfotext',
  'modal/info/additionalcoverage/text/sv',
  // quote-page insurance summary info popup (next to "Verzekering") — title + content
  'summaryinfotitle',
  'summaryinfotext',
])

// Labels a provider may override but that otherwise fall back to the shared row: the main-coverage
// option names, whose keys are the provider's configured option keys (see insuranceCoverageOptions).
const INSURANCE_PROVIDER_OVERRIDABLE_LABEL_PREFIXES = ['modal/insurancetype/']

// Coverage names longer than this get the wider modal layout.
const WIDE_COVERAGE_NAME_LENGTH = 25

// Only reached when the provider configuration cannot be loaded, so the dropdown is never empty.
const FALLBACK_COVERAGE_OPTIONS = Object.freeze([
  { key: 'ca', code: 'ca' },
  { key: 'wa', code: 'wa' },
  { key: 'bc', code: 'bc' },
])

export default {
  components: {
    Modal,
    SummaryBlock,
    InsuranceForm,
    ConfirmationPrompt,
    DatePicker,
    InfoModal,
    FormInput,
    FormTextarea,
  },
  mixins: [FinancialServiceBlock],
  provide() {
    return {
      // Properties
      paramKeys: this.paramKeys,
      // computed() keeps these reactive; a plain value is copied once when the modal is created
      isBusinessCustomer: computed(() => this.isBusinessCustomer),
      updateDateOfBirth: this.updateDateOfBirth,
      disableFields: computed(() => this.disableFields),
      minDate: this.minDate,
    }
  },
  props: {
    brandIsBwg: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const defaultDamageFreeYears = '8'
    const defaultDateOfBirth = '1975-01-01'
    const defaultZipCode = '1000AZ'
    const additionalCoverages = {
      sv: {
        selected: false,
        code: 'sv',
      },
      rp: {
        selected: false,
        code: 'rp',
        specification: 'UITGEBREIDE',
      },
      ph: {
        selected: false,
        code: 'ph',
      },
    }

    const additionalCoveragesArray = Object.entries(additionalCoverages).map((x) => x[1])
    return {
      showInsuranceRequiredParameterPrompt: false,
      showCompanyDataCorrectPrompt: false,
      searchCompanyIsPending: false,
      defaultDamageFreeYears: defaultDamageFreeYears,
      defaultDateOfBirth: defaultDateOfBirth,
      extraAccessoriesValue: 2500,
      extraAccessoriesThreshold: 3500,
      modalDefaults: {
        newValueRegulation: 'N12',
        extraAccessoriesDefault: false,
        autoCheckAccessories: true,
        mainCoverageOption: null,
      },
      insuranceProvider: 'vwpfs',
      // Main-coverage options configured for the provider; the commercial list serves grey licence
      // plates, which send no nieuwwaarde garantie and therefore need options that do not fix one.
      coverageOptions: [],
      commercialCoverageOptions: [],
      selectedCoverageOptionKey: null,
      mainCoveragePremiums: {},
      providerConfigurationPromise: null,
      minDate: moment('1900-01-01'),
      firstOpen: true, // Has quote insurance modal been opened before?
      afterInsuranceRequiredParameterConfirmAction: null,
      afterInsuranceRequiredParameterCancelAction: null,
      // Properties from modal
      financialParams: {
        // Default params
        policyHolder: {
          address: {
            zipCode: defaultZipCode,
          },
          dateOfBirth: defaultDateOfBirth, //yyyy-mm-dd
          cocNumber: '',
          sbiCode: '',
          zzp: false,
          sbiCodeText: '',
          companyName: '',
        },
        contract: {
          mileage: 10,
          paymentTerm: 1,
          damageFreeYears: defaultDamageFreeYears,
          newValueRegulation: 'N12',
          voluntaryDeductable: 0,
        },
        vehicle: {
          constructionMonth: 1,
          usage: 'P',
          extraAccessories: 0,
        },
        coverages: {
          mainCoverages: [
            {
              selected: true,
              code: 'ca',
            },
          ],
          additionalCoverages: additionalCoveragesArray,
        },
        // Key of the selected configured main-coverage option; PCQM stores it with the call.
        coverageOption: null,
        insurancePrice: null,
        noInsuranceAvailable: false,
        errors: null,
        alarmClass: null,
      },
      options: {
        customerType: ['P', 'Z'], // personal, business
        customerTypeCommercial: ['EIGEN', 'BEZORG', 'VERHUUR', 'PERSONENVERVOER'], // own usage
        mileage: [10, 15, 20, 99],
        voluntaryDeductable: [0, 250, 500, 750, 1000],
        newValueRegulation: ['N12', 'N36', 'N60'],
        paymentTerm: [1, 3, 6, 12],
        additionalCoverages: additionalCoverages,
        roadsideAssistanceRegions: ['NL_AANH', 'EU_AANH'],
        cargoInsuranceAmounts: ['€ 5.000', '€ 10.000'],
      },
      currentDate: Date.now(),
      // Keep track of roadside assistance region selection
      roadsideAssistanceRegion: 'NL_AANH',
      cargoInsuranceAmount: '€ 5.000',
      cargoInsuranceOptionName: 'tr',
      prevHasCustomerCompany: false,
    }
  },
  computed: {
    ...mapGetters({
      isReadOnly: 'quote/quoteStatusIsSalesOrder',
      quoteStatusChangeDate: 'quote/quoteStatusChangeDate',
      quoteShowInPdf: 'quote/showVwpfsInsurancePdf',
      latestVwpfsInsuranceCall: 'quote/latestVwpfsInsuranceCall',
      customerZipcode: 'quote/customerZipcode',
      dealerZipcode: 'quote/dealerZipcode',
      totalPrice: 'quote/totalPrice',
      accessoriesWithCategories: 'quote/accessoriesWithCategories',
      hasCustomer: 'quote/hasCustomer',
      hasCustomerCompany: 'quote/hasCustomerCompany',
      hasOwner: 'quote/hasOwner',
      customerCompany: 'quote/customerCompany',
      showVwpfsFinancePdf: 'quote/showVwpfsFinancePdf',
      showVwpfsPrivateLeasePdf: 'quote/showVwpfsPrivateLeasePdf',
      showVwpfsInsurancePdf: 'quote/showVwpfsInsurancePdf',
      showVwpfsOperationalLeasePdf: 'quote/showVwpfsOperationalLeasePdf',
      contractor: 'quote/printOptions/contractor',
      contactInfoArray: 'quote/printOptions/contactInfo',
      configuration: 'configuration/configuration',
    }),
    dealerAccessoriesTotalPrice() {
      return this.accessoriesWithCategories(['DealerAccessories'])?.reduce(
        (acc, item) => acc + CurrencyHelper.fromString(item.Price?.Incl),
        0
      )
    },
    paramKeys() {
      return Object.freeze({
        customerType: 'customerType',
        dateOfBirth: 'dateOfBirth',
        zipCode: 'zipCode',
        mileage: 'mileage',
        damageFreeYears: 'damageFreeYears',
        mainCoverageType: 'mainCoverageType',
        voluntaryDeductable: 'voluntaryDeductable',
        newValueRegulation: 'newValueRegulation',
        additionalCoverages: 'additionalCoverages',
        roadsideAssistanceRegion: 'roadsideAssistanceRegion',
        cargoInsuranceAmount: 'cargoInsuranceAmount',
        extraAccessories: 'extraAccessories',
        cocNumber: 'cocNumber',
        sbiCode: 'sbiCode',
        zzp: 'zzp',
        sbiCodeText: 'sbiCodeText',
        companyName: 'companyName',
      })
    },
    isGreyLicencePlate() {
      return (
        this.brandIsBwg &&
        (this.configuration?.BpmCode?.startsWith('B') ||
          this.configuration?.BpmCode?.startsWith('N'))
      )
    },
    activeCoverageOptions() {
      if (this.isGreyLicencePlate && this.commercialCoverageOptions.length) {
        return this.commercialCoverageOptions
      }
      return this.coverageOptions.length ? this.coverageOptions : FALLBACK_COVERAGE_OPTIONS
    },
    selectedCoverageOption() {
      return this.activeCoverageOptions.find((x) => x.key === this.selectedCoverageOptionKey)
    },
    newValueRegulationFixed() {
      return !!this.selectedCoverageOption?.newValueRegulation
    },
    wideCoverageNames() {
      return this.productTypes.mainCoverageType.some(
        (x) => (x.name || '').length > WIDE_COVERAGE_NAME_LENGTH
      )
    },
    productTypes() {
      const additionalCoveragesOptions = {}
      Object.keys(this.options.additionalCoverages).forEach((x) => {
        const opt = this.options.additionalCoverages[x]
        additionalCoveragesOptions[x] = {
          name: this.retrieveModalText(`additionalCoverage/${opt.code}`),
          value: opt.code,
          premium: opt.premium,
          possible: opt.possible,
        }
      })
      additionalCoveragesOptions.ph.specifications = this.options.roadsideAssistanceRegions.map(
        (x) => ({
          name: this.retrieveModalText(`roadsideAssistanceRegion/${x}`),
          value: `${x}`,
        })
      )
      if (this.isGreyLicencePlate && additionalCoveragesOptions.tr) {
        additionalCoveragesOptions.tr.specifications = this.options.cargoInsuranceAmounts.map(
          (x) => ({
            name: `${x}`,
            value: `${x}`,
          })
        )
      }

      let customerTypeOptions = this.isGreyLicencePlate
        ? this.options.customerTypeCommercial
        : this.options.customerType

      return {
        customerType: customerTypeOptions.map((x) => ({
          name: this.retrieveModalText(`customerType/${x}`),
          value: x,
        })),
        mileage: this.options.mileage.map((x) => ({
          name:
            x === 99
              ? this.retrieveModalText('unlimitedmileage')
              : this.retrieveModalText(`mileage/${x * 1000}`),
          value: `${x}`,
        })),
        // Premiums come per coverage code; options sharing a code all show that code's premium.
        mainCoverageType: this.activeCoverageOptions.map((x) => ({
          name: this.retrieveModalText(`insuranceType/${x.key}`),
          value: x.key,
          code: x.code,
          premium: this.mainCoveragePremiums[x.code.toLowerCase()],
        })),
        voluntaryDeductable: this.options.voluntaryDeductable.map((x) => ({
          name: this.i18n.n(x, 'currencyRounded'),
          value: `${x}`,
        })),
        newValueRegulation: this.options.newValueRegulation.map((x) => ({
          name: this.retrieveModalText(`newValueRegulationType/${x}`),
          value: x,
        })),
        additionalCoverages: additionalCoveragesOptions,
      }
    },
    computedFinancialParams() {
      return {
        [this.paramKeys.customerType]: this.financialParams.vehicle.usage,
        age: this.calculateAge(
          new Date(this.financialParams.policyHolder.dateOfBirth),
          this.currentDate
        ),
        [this.paramKeys.dateOfBirth]: this.financialParams.policyHolder.dateOfBirth,
        [this.paramKeys.zipCode]: this.financialParams.policyHolder.address.zipCode,
        [this.paramKeys.mileage]: `${this.financialParams.contract?.mileage}`,
        [this.paramKeys.damageFreeYears]: this.financialParams.contract?.damageFreeYears,
        [this.paramKeys.mainCoverageType]: this.selectedCoverageOptionKey,
        [this.paramKeys.voluntaryDeductable]:
          `${this.financialParams.contract.voluntaryDeductable}`,
        [this.paramKeys.newValueRegulation]: this.financialParams.contract.newValueRegulation,
        insurancePrice: this.financialParams.insurancePrice,
        [this.paramKeys.additionalCoverages]: this.financialParams.coverages.additionalCoverages
          ?.filter((x) => x.selected === true)
          ?.map((x) => x.code),
        [this.paramKeys.roadsideAssistanceRegion]: this.roadsideAssistanceRegion,
        [this.paramKeys.cargoInsuranceAmount]: this.cargoInsuranceAmount,
        [this.paramKeys.extraAccessories]: this.financialParams.vehicle.extraAccessories,
        [this.paramKeys.cocNumber]: this.financialParams.policyHolder.cocNumber,
        [this.paramKeys.sbiCode]: this.financialParams.policyHolder.sbiCode,
        [this.paramKeys.zzp]: this.financialParams.policyHolder.zzp,
        [this.paramKeys.sbiCodeText]: this.financialParams.policyHolder.sbiCodeText,
        [this.paramKeys.companyName]: this.financialParams.policyHolder.companyName,
        alarmClass: this.financialParams.alarmClass,
        errors: this.financialParams.errors,
      }
    },
    financingSummary() {
      return {
        title: this.retrieveText('sectiontitle'),
        summaryLine: this.retrieveText('summaryline', {
          mainCoverageType: this.getSelectedProduct(
            'mainCoverageType',
            this.computedFinancialParams.mainCoverageType
          )?.name,
          age: this.computedFinancialParams.age,
          zipCode: this.computedFinancialParams.zipCode,
          mileage: this.getSelectedProduct('mileage', this.computedFinancialParams.mileage)?.name,
          damageFreeYears: this.computedFinancialParams.damageFreeYears,
        }),
        rateTotal: this.financialParams.insurancePrice
          ? CurrencyHelper.currencyRounded(this.financialParams.insurancePrice)
          : '-',
        infoModalTitle: this.retrieveText('summaryinfotitle'),
        infoModalContent: this.retrieveText('summaryinfotext'),
        showInPdfCheckboxLabel: this.retrieveText('showVwpfsFinancePdf Label'),
        openModalButtonLabel: this.retrieveText('openmodalbutton'),
        isReadOnly: this.isReadOnly,
        showInPdf: this.showInPdf,
      }
    },
    productTypeDictionaryFolder() {
      return 'info'
    },
    isBusinessCustomer() {
      return (
        this.computedFinancialParams.customerType === 'Z' ||
        this.computedFinancialParams.customerType === 'EIGEN'
      )
    },
    disableFields() {
      return this.isReadOnly || this.financialParams.noInsuranceAvailable
    },
    isEigenCustomer() {
      return this.computedFinancialParams.customerType === 'EIGEN'
    },
    financialParamsCargoInsuranceOption() {
      return this.financialParams?.coverages?.additionalCoverages?.find(
        (x) => x.code === this.cargoInsuranceOptionName
      )
    },
    isAllCompanyFieldsFilled() {
      return (
        !!this.financialParams.policyHolder.cocNumber &&
        !!this.financialParams.policyHolder.sbiCode &&
        !!this.financialParams.policyHolder.sbiCodeText
      )
    },
    allRequiredFieldsProvided() {
      return (
        this.computedFinancialParams[this.paramKeys.dateOfBirth] &&
        this.computedFinancialParams[this.paramKeys.damageFreeYears] &&
        !(this.isGreyLicencePlate && !this.computedFinancialParams[this.paramKeys.cocNumber])
      )
    },
  },
  watch: {
    isGreyLicencePlate(newValue) {
      if (newValue) {
        this.updateVehicleUsage()
        this.updateAdditionalCoveragesArray()
      }
    },
    activeCoverageOptions(options) {
      if (options.length && !options.some((x) => x.key === this.selectedCoverageOptionKey)) {
        const code = this.selectedMainCoverageCode()
        this.selectCoverageOption((options.find((x) => x.code === code) || options[0]).key)
      }
    },
    hasCustomer() {
      this.updateVehicleUsage()
    },
    hasCustomerCompany() {
      this.updateVehicleUsage()
    },
    dealerAccessoriesTotalPrice(newTotal, oldTotal) {
      if (oldTotal < this.extraAccessoriesThreshold && newTotal >= this.extraAccessoriesThreshold) {
        this.checkAccessoriesTotal()
      }
    },
  },
  mounted() {
    // Initialise values
    // If order salesorder, use date when order was created as current date to calculate age
    if (this.isReadOnly && this.quoteStatusChangeDate) {
      this.currentDate = this.quoteStatusChangeDate
    }

    EventBus.$once('set-insurance-data', () => {
      if (this.tempLatestVwpfsCalls?.LatestVwpfsInsuranceCall) {
        this.$store
          .dispatch(
            'quote/setLatestVwpfsInsuranceCall',
            this.tempLatestVwpfsCalls.LatestVwpfsInsuranceCall
          )
          .then(() => {
            this.$store.commit('quote/clearTempLatestVwpfsCalls', 'LatestVwpfsInsuranceCall')
            this.requestFinancing()
          })
      } else if (!this.financialParams.errors && !this.financialParams.noPrivateLeaseAvailable)
        this.$store.dispatch('quote/setLatestVwpfsInsuranceCall', {
          response: {
            insurance: {
              contract: {
                premium: {
                  invoiceAmount: this.financialParams.insurancePrice,
                },
              },
            },
          },
          responseCode: 200,
        })
    })
    EventBus.$on('configuration-updated', this.requestFinancing)
    EventBus.$on('quote/customerUpdated', this.setZipCodeAndUpdate)
    EventBus.$on('quote/customerRemoved', this.setZipCodeAndUpdate)
    EventBus.$on('quote/companyUpdated', this.companyUpdated)
    EventBus.$on('quote/companyRemoved', this.companyRemoved)
    this.updateVehicleUsage()
    this.updateAdditionalCoveragesArray()
  },
  unmounted() {
    EventBus.$off('set-insurance-data')
    EventBus.$off('configuration-updated', this.requestFinancing)
    EventBus.$off('quote/customerUpdated', this.setZipCodeAndUpdate)
    EventBus.$off('quote/customerRemoved', this.setZipCodeAndUpdate)
    EventBus.$off('quote/companyUpdated', this.companyUpdated)
    EventBus.$off('quote/companyRemoved', this.companyRemoved)
  },
  methods: {
    ...mapActions({
      setCustomerCompany: 'quote/setCustomerCompanyWithoutSaving',
    }),
    async requestFinancing() {
      this.applyGreyLicencePlateDefaults()

      // Check whether insurance is disabled because not every checkbox condition is fulfilled
      if (this.isGreyLicencePlate && !this.isEigenCustomer) return this.setFinancingIsLoading(false)
      if (this.isGreyLicencePlate && this.firstOpen && !this.isAllCompanyFieldsFilled)
        return this.setFinancingIsLoading(false)

      this.setFinancingIsLoading(true)

      const request = { quoteId: this.quote?.QuoteId, ...this.financialParams }
      const response = await this.fetchInsurance(request)
      if (!response) return

      this.applyInsuranceResponse(response, request)
    },
    applyGreyLicencePlateDefaults() {
      if (!this.isGreyLicencePlate) return
      this.financialParams.contract.newValueRegulation = null
      this.extraAccessoriesValue = 3000
      this.extraAccessoriesThreshold = 2000
    },
    applyInsuranceResponse(response, request) {
      this.$store.dispatch('quote/setLatestVwpfsInsuranceCall', {
        response: { ...response },
        request: { ...request },
        responseCode: 200,
      })

      // The vehicle cannot be insured at all.
      if (response.noInsuranceAvailable) {
        this.financialParams.insurancePrice = null
        this.financialParams.errors = null
        this.financialParams.noInsuranceAvailable = true
        this.setFinancingIsLoading(false)
        return
      }

      // The provider declined with a reason of its own, e.g. no premium for these parameters.
      if (response.informationMessage) {
        this.financialParams.errors = [response.informationMessage]
        this.setFinancingIsLoading(false)
        return
      }

      this.financialParams.errors = null
      this.financialParams.noInsuranceAvailable = false
      this.financialParams.contract.mileage = this.convertMileageStringToInt(
        this.financialParams.contract.mileage
      )
      if (!this.financialParams.alarmClass) {
        this.financialParams.alarmClass = response?.insurance?.alarmClass
      }
      this.financialParams.insurancePrice = response?.insurance?.contract?.premium?.invoiceAmount
      const responseCoverages = response?.insurance?.coverages
      if (responseCoverages?.mainCoverages) {
        this.setMainCoverageType(responseCoverages.mainCoverages)
      }
      if (responseCoverages?.additionalCoverages) {
        this.setAdditionalCoverageOptions(responseCoverages.additionalCoverages)
      }
      this.setFinancingIsLoading(false)
    },
    async fetchInsurance(request) {
      return FinancialServices.setInsurance({
        ...request,
        parametersAccepted: !this.firstOpen,
      }).catch((error) => {
        this.financialParams.insurancePrice = null
        this.$store.dispatch('quote/setLatestVwpfsInsuranceCall', {
          responseCode: error.response?.status,
        })
        this.financialParams.errors = this.errorMessagesFrom(error)
        this.setFinancingIsLoading(false)
        return null
      })
    },
    errorMessagesFrom(error) {
      const data = error.response?.data
      if (data?.messages?.length) return data.messages
      return data?.message ? [data.message] : null
    },
    async initFinancing() {
      await this.ensureProviderConfigurationLoaded()

      if (this.isGreyLicencePlate) {
        this.financialParams.contract.newValueRegulation = null
        this.extraAccessoriesValue = 3000
        this.extraAccessoriesThreshold = 2000
      }

      this.showInPdf = this.quoteShowInPdf
      if (!!this.latestVwpfsInsuranceCall) {
        this.setFinancingIsLoading(true)

        const latestVwpfsInsuranceCallData = this.latestVwpfsInsuranceCall
        const latestVwpfsInsuranceCallRequestData = latestVwpfsInsuranceCallData?.request?.insurance
        this.financialParams.alarmClass = latestVwpfsInsuranceCallData?.alarmClass
        this.firstOpen = !(
          latestVwpfsInsuranceCallData?.damageFreeYearsAccepted &&
          latestVwpfsInsuranceCallData?.dateOfBirthAccepted
        )

        // Additional coverages should always be sent, so prevent it from being overwritten if latestCall contains null or empty
        // Only do this if we have latest request data
        if (
          latestVwpfsInsuranceCallRequestData &&
          !latestVwpfsInsuranceCallRequestData.coverages.additionalCoverages?.length
        ) {
          latestVwpfsInsuranceCallRequestData.coverages.additionalCoverages =
            this.financialParams.coverages.additionalCoverages
        }

        // update properties from latest call data
        // Use extend so that other properties aren't temporarily deleted
        this.financialParams = extend(this.financialParams, latestVwpfsInsuranceCallRequestData)
        this.financialParams.contract.mileage = this.convertMileageStringToInt(
          this.financialParams.contract.mileage
        )
        this.restoreCoverageOption(latestVwpfsInsuranceCallData?.coverageOption)
        if (this.firstOpen) {
          this.checkAccessoriesTotal()
        }
        const roadsideAssistanceOption = this.financialParams.coverages.additionalCoverages.find(
          (x) => x.code === this.options.additionalCoverages.ph.code
        )
        if (roadsideAssistanceOption) {
          this.roadsideAssistanceRegion = roadsideAssistanceOption.specification
        }

        if (this.financialParamsCargoInsuranceOption) {
          this.cargoInsuranceAmount = this.financialParamsCargoInsuranceOption.specification
        }

        if (!this.isReadOnly) {
          this.requestFinancing(true)
        } else {
          const latestVwpfsInsuranceCallResponseData =
            latestVwpfsInsuranceCallData?.response?.insurance
          if (latestVwpfsInsuranceCallResponseData) {
            this.financialParams.insurancePrice =
              latestVwpfsInsuranceCallResponseData?.contract?.premium?.invoiceAmount
            const latestCoverages = latestVwpfsInsuranceCallResponseData?.coverages
            if (latestCoverages) {
              this.setMainCoverageType(latestCoverages.mainCoverages)
              this.setAdditionalCoverageOptions(latestCoverages.additionalCoverages)
            }
          } else if (latestVwpfsInsuranceCallData?.response?.noInsuranceAvailable) {
            this.financialParams.noInsuranceAvailable = true
          } else {
            // Error
            const informationMessage = latestVwpfsInsuranceCallData?.response?.informationMessage
            this.financialParams.errors = informationMessage ? [informationMessage] : null
          }
          this.setFinancingIsLoading(false)
        }
      } else {
        // Fresh modal (no saved call): show provider defaults before the first calculation.
        this.applyModalDefaults()
        if (this.financialParams.policyHolder.address.zipCode) {
          this.requestFinancing()
        }
      }

      // Call financing after any price change, prevent double update when config is changed
      this.$watch('totalPrice.Excl', () => {
        if (!this.configurationIsUpdating) {
          this.requestFinancing()
        }
      })
    },
    changeCocNumber(cocNumber) {
      this.updateFinancingParams(this.paramKeys.cocNumber, cocNumber, false)
    },
    async searchCocNumber(cocNumber, requestFinancing = false) {
      let sbiData = {}
      if (!!cocNumber) {
        sbiData = await Companies.SearchCompanySbiCode({
          cocNumber: cocNumber,
        })
      }

      this.changeCocNumber(cocNumber)

      this.updateFinancingParams(this.paramKeys.sbiCode, sbiData.sbiCode || '', false)

      this.updateFinancingParams(this.paramKeys.sbiCodeText, sbiData.sbiCodeText || '', false)

      if (requestFinancing) {
        this.requestFinancing()
      }
    },
    async changeShowInPdf(showInPdf) {
      await this.$store.dispatch('quote/setShowVwpfsInsurancePdf', showInPdf)

      const pdfOptions = {
        quoteId: this.quote?.QuoteId,
        printContractorOnOrder: this.contractor,
        printContactInfoOnPdf: this.contactInfoArray,
        showVwpfsFinancePdf: this.showVwpfsFinancePdf,
        showVwpfsPrivateLeasePdf: this.showVwpfsPrivateLeasePdf,
        showVwpfsInsurancePdf: this.showVwpfsInsurancePdf,
        showVwpfsOperationalLeasePdf: this.showVwpfsOperationalLeasePdf,
      }

      await this.$store.dispatch('quote/savePrintOptions', pdfOptions)
    },
    getSelectedProduct(type, value) {
      return this.productTypes[type].find(
        (x) => `${x.value}`.toLowerCase() === `${value}`.toLowerCase()
      )
    },
    retrieveText(labelName, params = null) {
      // Provider-specific texts are namespaced per provider with no cross-provider fallback: if the
      // active provider's copy is missing, render nothing rather than another provider's text (or
      // the raw key). Overridable labels use the provider's copy when it exists and the shared row
      // otherwise. All other labels resolve as shared/brand-based.
      const providerKey = `/financialservices/insurance/${this.insuranceProvider}/${labelName}`
      if (INSURANCE_PROVIDER_SPECIFIC_LABELS.has(labelName)) {
        return this.i18n.translationExists(providerKey) ? this.i18n.t(providerKey, params) : ''
      }
      if (
        INSURANCE_PROVIDER_OVERRIDABLE_LABEL_PREFIXES.some((prefix) =>
          labelName.toLowerCase().startsWith(prefix)
        ) &&
        this.i18n.translationExists(providerKey)
      ) {
        return this.i18n.t(providerKey, params)
      }
      return this.i18n.t(`/financialservices/insurance/${labelName}`, params)
    },
    calculateAge(dateOfBirth, currentDate) {
      return moment(currentDate).diff(moment(dateOfBirth), 'years')
    },
    updateFinancingParams(param, value, requestFinancing = true) {
      // map param names to request object structure
      switch (param) {
        case this.paramKeys.customerType:
          this.financialParams.vehicle.usage = value
          break
        case this.paramKeys.dateOfBirth:
          this.financialParams.policyHolder.dateOfBirth = value
          break
        case this.paramKeys.zipCode:
          this.financialParams.policyHolder.address.zipCode = value
          break
        case this.paramKeys.mileage:
          this.financialParams.contract.mileage = parseInt(value)
          break
        case this.paramKeys.damageFreeYears:
          this.financialParams.contract.damageFreeYears = value
          break
        case this.paramKeys.mainCoverageType:
          this.selectCoverageOption(value)
          break
        case this.paramKeys.voluntaryDeductable:
          this.financialParams.contract.voluntaryDeductable = parseInt(value)
          break
        case this.paramKeys.newValueRegulation:
          this.financialParams.contract.newValueRegulation = value
          break
        case this.paramKeys.additionalCoverages:
          this.financialParams.coverages.additionalCoverages = Object.entries(
            this.options.additionalCoverages
          ).map((x) => {
            const coverage = x[1]
            coverage.selected = value.includes(coverage.code)
            return coverage
          })
          this.setRoadsideAssistanceRegion()
          this.setCargoInsuranceAmount()
          break
        case this.paramKeys.extraAccessories:
          this.financialParams.vehicle.extraAccessories = value ? this.extraAccessoriesValue : 0
          break
        case this.paramKeys.roadsideAssistanceRegion:
          this.roadsideAssistanceRegion = value
          this.setRoadsideAssistanceRegion()
          break
        case this.paramKeys.cargoInsuranceAmount:
          this.cargoInsuranceAmount = value
          this.setCargoInsuranceAmount()
          break
        case this.paramKeys.companyName:
          this.financialParams.policyHolder.companyName = value
          break
        case this.paramKeys.cocNumber:
          this.financialParams.policyHolder.cocNumber = value
          break
        case this.paramKeys.sbiCode:
          this.financialParams.policyHolder.sbiCode = value
          this.setCustomerCompanySbiCode()
          break
        case this.paramKeys.sbiCodeText:
          this.financialParams.policyHolder.sbiCodeText = value
          this.setCustomerCompanySbiCodeText()
          break
      }
      if (requestFinancing) {
        this.requestFinancing()
      }
    },
    setMainCoverageType(options) {
      this.mainCoveragePremiums = Object.fromEntries(
        options.map((x) => [x.code.toLowerCase(), x.total])
      )
    },
    selectedMainCoverageCode() {
      const mainCoverages = this.financialParams.coverages.mainCoverages
      return (mainCoverages.find((x) => x.selected === true) || mainCoverages[0])?.code
    },
    selectCoverageOption(key) {
      const option = this.activeCoverageOptions.find((x) => x.key === key)
      if (!option) return
      this.selectedCoverageOptionKey = key
      this.financialParams.coverageOption = key
      this.financialParams.coverages.mainCoverages = [{ selected: true, code: option.code }]
      if (option.newValueRegulation) {
        this.financialParams.contract.newValueRegulation = option.newValueRegulation
      }
    },
    restoreCoverageOption(storedKey) {
      // The stored key is missing on quotes calculated before PCQM recorded it.
      const key = this.activeCoverageOptions.some((x) => x.key === storedKey)
        ? storedKey
        : findCoverageOptionByCode(
            this.activeCoverageOptions,
            this.selectedMainCoverageCode(),
            this.financialParams.contract.newValueRegulation
          )?.key
      this.selectedCoverageOptionKey = key
      this.financialParams.coverageOption = key
    },
    setCustomerCompanySbiCode() {
      if (this.customerCompany) {
        this.customerCompany.CustomerCompanySbiCode = this.financialParams.policyHolder.sbiCode
        this.setCustomerCompany(this.customerCompany)
      }
    },
    setCustomerCompanySbiCodeText() {
      if (this.customerCompany) {
        this.customerCompany.CustomerCompanySbiName = this.financialParams.policyHolder.sbiCodeText
        this.setCustomerCompany(this.customerCompany)
      }
    },
    setAdditionalCoverageOptions(options) {
      options = options.filter(
        (opt) => opt.code.toLowerCase() != 'oi' && opt.code.toLowerCase() != 'dg'
      )
      options.forEach((opt) => {
        let code = opt.code.toLowerCase()

        this.options.additionalCoverages[code] = extend(this.options.additionalCoverages[code], {
          premium: opt.total,
          possible: opt.possible,
        })
      })
      this.forceCargoInsuranceOptionPossible()
    },
    setRoadsideAssistanceRegion() {
      const roadsideAssistanceOption = this.financialParams.coverages.additionalCoverages.find(
        (x) => x.code === this.options.additionalCoverages.ph.code
      )
      if (roadsideAssistanceOption) {
        roadsideAssistanceOption.specification = this.roadsideAssistanceRegion
      }
    },
    forceCargoInsuranceOptionPossible() {
      const cargoInsuranceOption = this.options.additionalCoverages.tr
      if (!!cargoInsuranceOption) {
        cargoInsuranceOption.possible = true
      }
    },
    setCargoInsuranceAmount() {
      if (this.financialParamsCargoInsuranceOption) {
        this.financialParamsCargoInsuranceOption.specification = this.cargoInsuranceAmount
      }
    },
    formatZipCode(zipCode) {
      return zipCode.replace(/\s/g, '').toUpperCase()
    },
    getZipCode() {
      if (this.customerZipcode) {
        return this.customerZipcode
      }
      if (!this.hasCustomer && this.customerCompany?.CustomerCompanyZipcode) {
        return this.customerCompany.CustomerCompanyZipcode
      }
      return null
    },
    companyUpdated() {
      this.updateCompanyName()
      this.updateCompanyChamberofCommerse(true)
      this.checkCustomerAvailability()
    },
    companyRemoved() {
      this.updateCompanyName()
      this.updateCompanyChamberofCommerse(true)
      this.setZipCodeAndUpdate()
    },
    updateCompanyName() {
      if (this.isGreyLicencePlate) {
        this.financialParams.policyHolder.companyName =
          this.customerCompany?.CustomerCompanyName || ''
      }
    },
    updateCompanyChamberofCommerse(doSearch = false) {
      if (this.isGreyLicencePlate) {
        const chamberOfCommerceNumber =
          this.customerCompany?.CustomerCompanyChamberOfCommerceNumber || ''

        this.financialParams.policyHolder.cocNumber = chamberOfCommerceNumber

        if (doSearch) {
          this.searchCocNumber(chamberOfCommerceNumber, false)
        }
      }
    },
    checkCustomerAvailability() {
      if (this.hasCustomer) {
        return
      } else {
        this.setZipCodeAndUpdate()
      }
    },
    setZipCodeAndUpdate() {
      const zipCode = this.getZipCode()
      if (zipCode) {
        this.financialParams.policyHolder.address.zipCode = this.formatZipCode(zipCode)
      }

      if (this.hasOwner) {
        this.requestFinancing()
      }
    },
    updateDateOfBirth(value, requestFinancing = true) {
      this.updateFinancingParams(
        this.paramKeys.dateOfBirth,
        value.format('YYYY-MM-DD'),
        requestFinancing
      )
    },
    closeConfigModal() {
      this.showConfigModal(false)
    },
    async onInsuranceRequiredParameterPromptConfirm() {
      if (this.isGreyLicencePlate) {
        this.searchCompanyIsPending = true
        await this.searchCocNumber(this.financialParams.policyHolder.cocNumber, false)
        this.searchCompanyIsPending = false
        this.showCompanyDataCorrectPrompt = true
      } else {
        this.onCompanyDataCorrectPromptConfirm()
      }
    },
    onCompanyDataCorrectPromptConfirm() {
      this.firstOpen = false

      this.showCompanyDataCorrectPrompt = false
      this.setZipCodeAndUpdate()
      if (this.afterInsuranceRequiredParameterConfirmAction) {
        this.afterInsuranceRequiredParameterConfirmAction()
      }
      this.closeInsuranceRequiredParameterPrompt()
      this.showConfigModal(true)
    },
    onCompanyDataCorrectPromptCancel() {
      this.showCompanyDataCorrectPrompt = false
    },
    closeInsuranceRequiredParameterPrompt(isCancel = false) {
      if (isCancel && this.afterInsuranceRequiredParameterCancelAction) {
        this.afterInsuranceRequiredParameterCancelAction()
      }
      this.afterInsuranceRequiredParameterConfirmAction = null
      this.afterInsuranceRequiredParameterCancelAction = null
      this.showInsuranceRequiredParameterPrompt = false
    },
    checkAccessoriesTotal() {
      // Auto-ticks the extra-accessories coverage once the dealer accessories total exceeds the
      // free-insured limit; providers can disable this via config.
      if (!this.modalDefaults.autoCheckAccessories) {
        return
      }
      if (!this.financialParams.vehicle.extraAccessories) {
        this.financialParams.vehicle.extraAccessories = this.extraAccessoriesValue
      }
    },
    applyModalDefaults() {
      // Grey licence plates keep their own newValueRegulation handling (set to null in initFinancing).
      if (!this.isGreyLicencePlate) {
        this.financialParams.contract.newValueRegulation = this.modalDefaults.newValueRegulation
      }
      this.financialParams.vehicle.extraAccessories = this.modalDefaults.extraAccessoriesDefault
        ? this.extraAccessoriesValue
        : 0
      const options = this.activeCoverageOptions
      const defaultOption =
        options.find((x) => x.key === this.modalDefaults.mainCoverageOption) || options[0]
      if (defaultOption) {
        this.selectCoverageOption(defaultOption.key)
      }
    },
    ensureProviderConfigurationLoaded() {
      // Cache the in-flight promise so concurrent initFinancing calls share one fetch and
      // all await the same result before seeding defaults.
      if (!this.providerConfigurationPromise) {
        this.providerConfigurationPromise = this.loadProviderConfiguration()
      }
      return this.providerConfigurationPromise
    },
    async loadProviderConfiguration() {
      try {
        const rows = await FinancialServices.getInsuranceConfiguration()
        const provider = (rows || []).find((row) => !!row.provider)?.provider
        if (provider) {
          this.insuranceProvider = provider.toLowerCase()
        }

        const defaults = (rows || [])
          .filter((row) => row.configurationType === 'MODAL_DEFAULT' && row.isActive !== false)
          .reduce((acc, row) => ({ ...acc, [row.configurationKey]: row.configurationValue }), {})

        if (defaults.newValueRegulation) {
          this.modalDefaults.newValueRegulation = defaults.newValueRegulation
        }
        if (defaults.extraAccessoriesDefault !== undefined) {
          this.modalDefaults.extraAccessoriesDefault = defaults.extraAccessoriesDefault === 'true'
        }
        if (defaults.autoCheckAccessories !== undefined) {
          this.modalDefaults.autoCheckAccessories = defaults.autoCheckAccessories === 'true'
        }
        if (defaults.mainCoverageOption) {
          this.modalDefaults.mainCoverageOption = defaults.mainCoverageOption
        }

        this.coverageOptions = parseCoverageOptions(rows, 'COVERAGE_MAIN')
        this.commercialCoverageOptions = parseCoverageOptions(rows, 'COVERAGE_MAIN_COMMERCIAL')
      } catch {
        // Keep the built-in modal defaults and FALLBACK_COVERAGE_OPTIONS when no provider config
        // is available.
      }
    },
    convertMileageStringToInt(mileage) {
      // VWPFS returns mileage as "xx.y" instead of an int
      if (typeof mileage === 'number') {
        return mileage
      }
      mileage = parseInt(mileage)
      return mileage
    },
    setShowInPdfTrue() {
      this.changeShowInPdf(true)
    },
    uncheckShowInPdf() {
      this.showInPdf = false
    },
    toggleShowInPdf(showInPdf) {
      this.showInPdf = showInPdf
      if (showInPdf) {
        this.checkInsuranceRequiredParameters(this.setShowInPdfTrue, this.uncheckShowInPdf)
      } else {
        this.changeShowInPdf(showInPdf)
      }
    },
    openConfigModal() {
      this.showConfigModal(true)
    },
    checkInsuranceRequiredParameters(
      afterInsuranceRequiredParameterConfirmAction = null,
      afterInsuranceRequiredParameterCancelAction = null
    ) {
      // Check if the modal has been opened before and damage free years is changed
      if (
        this.firstOpen &&
        this.financialParams.contract.damageFreeYears === this.defaultDamageFreeYears &&
        this.financialParams.policyHolder.dateOfBirth === this.defaultDateOfBirth
      ) {
        this.afterInsuranceRequiredParameterConfirmAction =
          afterInsuranceRequiredParameterConfirmAction
        this.afterInsuranceRequiredParameterCancelAction =
          afterInsuranceRequiredParameterCancelAction

        this.updateCompanyName()
        this.updateCompanyChamberofCommerse(false)

        this.showInsuranceRequiredParameterPrompt = true
      } else {
        if (afterInsuranceRequiredParameterConfirmAction) {
          afterInsuranceRequiredParameterConfirmAction()
        } else {
          this.showConfigModal(true)
        }
      }
    },
    updateVehicleUsage() {
      if (this.isGreyLicencePlate) {
        this.financialParams.vehicle.usage = 'EIGEN'
      } else if (!this.hasCustomer && this.hasCustomerCompany && !this.prevHasCustomerCompany) {
        this.financialParams.vehicle.usage = 'Z'
      } else if (
        (this.hasCustomer && this.hasCustomerCompany) ||
        (this.hasCustomer && !this.hasCustomerCompany)
      ) {
        this.financialParams.vehicle.usage = 'P'
      } else if (!this.hasCustomer && this.hasCustomerCompany) {
        this.financialParams.vehicle.usage = 'Z'
      }
      this.prevHasCustomerCompany = this.hasCustomerCompany
    },
    updateAdditionalCoveragesArray() {
      if (this.isGreyLicencePlate) {
        this.addCargoInsuranceOption()
      } else {
        this.removeCargoInsuranceOption()
      }
    },
    addCargoInsuranceOption() {
      if (!this.financialParamsCargoInsuranceOption) {
        let newCargoInsuranceOption = {
          selected: false,
          code: this.cargoInsuranceOptionName,
          specification: this.cargoInsuranceAmount,
        }
        this.financialParams.coverages.additionalCoverages.push(newCargoInsuranceOption)
        this.options.additionalCoverages.tr = newCargoInsuranceOption
      }
    },
    removeCargoInsuranceOption() {
      if (this.financialParamsCargoInsuranceOption) {
        const index = this.financialParams.coverages.additionalCoverages.indexOf(
          this.cargoInsuranceOption
        )
        if (index > -1) {
          this.financialParams.coverages.additionalCoverages.splice(index, 1)
        }
        this.options.additionalCoverages.tr = null
      }
    },
  },
}
</script>
<style lang="scss" scoped>
:deep(.prompt-body) {
  .form-item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 1rem;

    input,
    textarea {
      flex-basis: 15rem;
    }

    textarea {
      height: 5rem;
    }

    .info-icon {
      margin-right: 1rem;
    }

    & > label,
    & > .label > label {
      width: 16rem;
      display: inline-block;
    }
  }

  .no-info > .label {
    flex-basis: 19rem;
  }
}
</style>
